// AUTO-GENERATED from apps_games_stats/Plample_Degree_Software_Profiles.xlsx —
// do not edit by hand. Regenerate via apps_games_stats/generate_degree_catalog.py
// if the spreadsheet or v2/software.csv changes.
//
// Each degree pre-selects the catalog software in `softwareValues` when chosen
// in the questionnaire; `software` holds the full profile table shown to the
// user (including tools with no catalog mapping, whose catalogValues is empty).

export type DegreeSoftwareRow = {
  name: string;
  category: string;
  usage: string;
  source: string;
  /** Catalog software `value`s this row maps onto (empty = display-only). */
  catalogValues: string[];
};

export type DegreeOption = {
  value: string;
  label: string;
  summary: string;
  /** Constituent degree names covered by this group, semicolon-separated. */
  degrees: string;
  universities: string;
  software: DegreeSoftwareRow[];
  /** Deduped union of catalogValues — what selecting the degree pre-selects. */
  softwareValues: string[];
};

export const DEGREE_OPTIONS: DegreeOption[] = [
  {
    "value": "computer-science-software-engineering",
    "label": "Computer Science & Software Engineering",
    "summary": "Covers Computer Science, Computing, CS (with a Year in Industry/Placement) and related courses. Selecting this degree pre-fills the software typically used on the course — including Python, Java, SQL & databases (MySQL/PostgreSQL) — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Computer Science; Computing; CS (with a Year in Industry/Placement); CS with AI/Machine Learning; Software Engineering; Computer Systems Engineering; Computer Science Tripos (Cambridge); Informatics (Edinburgh)",
    "universities": "All 20",
    "software": [
      {
        "name": "Python",
        "category": "Core tools",
        "usage": "Core, from Year 1 - first language at most departments",
        "source": "U. Glasgow Computing Science UG, gla.ac.uk/undergraduate/degrees/computingscience",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "Java",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - object-oriented programming",
        "source": "U. Glasgow Computing Science UG, gla.ac.uk/undergraduate/degrees/computingscience",
        "catalogValues": [
          "java-jdk"
        ]
      },
      {
        "name": "C / C++",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - systems & lower-level programming",
        "source": "U. Kent Computer Science BSc, kent.ac.uk/courses/undergraduate/124/computer-science",
        "catalogValues": [
          "c-cpp-toolchain"
        ]
      },
      {
        "name": "Git / GitHub / GitLab",
        "category": "Core tools",
        "usage": "Core, from Year 1 - version control",
        "source": "Kingston Computer Science BSc, kingston.ac.uk/study/undergraduate/computer-science-bsc-hons",
        "catalogValues": [
          "git"
        ]
      },
      {
        "name": "Linux / Unix command line",
        "category": "Core tools",
        "usage": "Core, from Year 1 - lab machines & shell",
        "source": "The Student Room, 'What programming language is taught at your university?', thestudentroom.co.uk/showthread.php?t=1483421",
        "catalogValues": [
          "linux-wsl"
        ]
      },
      {
        "name": "IDEs - VS Code, IntelliJ IDEA, Eclipse",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Kingston Computer Science BSc, kingston.ac.uk/study/undergraduate/computer-science-bsc-hons",
        "catalogValues": [
          "visual-studio-code",
          "intellij-idea"
        ]
      },
      {
        "name": "SQL & databases (MySQL/PostgreSQL)",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2 - databases module",
        "source": "London Metropolitan Data Science BSc, londonmet.ac.uk/courses/undergraduate/data-science---bsc-hons",
        "catalogValues": [
          "sql-databases"
        ]
      },
      {
        "name": "JavaScript / Node.js / React",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - web development",
        "source": "Kingston Computer Science BSc, kingston.ac.uk/study/undergraduate/computer-science-bsc-hons",
        "catalogValues": [
          "nodejs-web"
        ]
      },
      {
        "name": "Docker / containers",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - software engineering",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "docker-desktop"
        ]
      },
      {
        "name": "Haskell / functional languages",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 1-2 (Cambridge, Imperial)",
        "source": "The Student Room, thestudentroom.co.uk/showthread.php?t=1483421",
        "catalogValues": [
          "visual-studio-code"
        ]
      },
      {
        "name": "Assembly (ARM/x86)",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 1-2 - computer architecture",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "visual-studio-code"
        ]
      },
      {
        "name": "Jupyter + NumPy / Pandas",
        "category": "Data / analysis tools",
        "usage": "Data, Year 2-3 - data & AI modules",
        "source": "London Metropolitan Data Science BSc, londonmet.ac.uk/courses/undergraduate/data-science---bsc-hons",
        "catalogValues": [
          "jupyter-anaconda"
        ]
      },
      {
        "name": "PyTorch / TensorFlow",
        "category": "Data / analysis tools",
        "usage": "Data, later years - AI/ML modules",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "pytorch-tensorflow"
        ]
      },
      {
        "name": "LaTeX / Overleaf",
        "category": "Writing & productivity",
        "usage": "Writing, Year 2-3 - reports & dissertation",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "overleaf"
        ]
      },
      {
        "name": "Microsoft Office / Google Workspace",
        "category": "Writing & productivity",
        "usage": "Writing, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365",
          "google-workspace"
        ]
      },
      {
        "name": "Unity / Unreal Engine",
        "category": "Elective-only",
        "usage": "Elective - games development",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "unity",
          "unreal-engine"
        ]
      },
      {
        "name": "AWS / Azure cloud",
        "category": "Elective-only",
        "usage": "Elective - cloud computing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "docker-desktop"
        ]
      },
      {
        "name": "Verilog / VHDL",
        "category": "Elective-only",
        "usage": "Elective - hardware/embedded",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "fpga-vivado"
        ]
      }
    ],
    "softwareValues": [
      "python",
      "java-jdk",
      "c-cpp-toolchain",
      "git",
      "linux-wsl",
      "visual-studio-code",
      "intellij-idea",
      "sql-databases",
      "nodejs-web",
      "docker-desktop",
      "jupyter-anaconda",
      "pytorch-tensorflow",
      "overleaf",
      "microsoft-365",
      "google-workspace",
      "unity",
      "unreal-engine",
      "fpga-vivado"
    ]
  },
  {
    "value": "data-science",
    "label": "Data Science",
    "summary": "Covers Data Science, Data Science and AI, Mathematics and Data Science and related courses. Selecting this degree pre-fills the software typically used on the course — including Python (Pandas, NumPy, scikit-learn), R / RStudio, Tableau / Power BI — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Data Science; Data Science and AI; Mathematics and Data Science; BSc Data Science (LSE)",
    "universities": "LSE, Imperial, Bath, Surrey + broad set of the broad-14 (per-uni verification pending)",
    "software": [
      {
        "name": "Python (Pandas, NumPy, scikit-learn)",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "U. Kent Data Science BSc, kent.ac.uk/courses/undergraduate/4407/data-science",
        "catalogValues": [
          "python",
          "jupyter-anaconda",
          "pytorch-tensorflow"
        ]
      },
      {
        "name": "R / RStudio",
        "category": "Core tools",
        "usage": "Core, from Year 1 - statistics & modelling",
        "source": "U. Kent Data Science BSc, kent.ac.uk/courses/undergraduate/4407/data-science",
        "catalogValues": [
          "r-rstudio"
        ]
      },
      {
        "name": "SQL",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - data querying",
        "source": "London Metropolitan Data Science BSc, londonmet.ac.uk/courses/undergraduate/data-science---bsc-hons",
        "catalogValues": [
          "sql-databases"
        ]
      },
      {
        "name": "Jupyter Notebooks",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "London Metropolitan Data Science BSc, londonmet.ac.uk/courses/undergraduate/data-science---bsc-hons",
        "catalogValues": [
          "jupyter-anaconda"
        ]
      },
      {
        "name": "Git",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "git"
        ]
      },
      {
        "name": "Tableau / Power BI",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2 - visualisation & dashboards",
        "source": "London Metropolitan Data Science BSc, londonmet.ac.uk/courses/undergraduate/data-science---bsc-hons",
        "catalogValues": [
          "tableau-desktop",
          "power-bi-desktop"
        ]
      },
      {
        "name": "Hadoop / Spark / Kafka",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - big data",
        "source": "London Metropolitan Data Science BSc, londonmet.ac.uk/courses/undergraduate/data-science---bsc-hons",
        "catalogValues": [
          "hadoop-spark"
        ]
      },
      {
        "name": "D3.js",
        "category": "Specialist / field-specific",
        "usage": "Specialist - interactive visualisation",
        "source": "London Metropolitan Data Science BSc, londonmet.ac.uk/courses/undergraduate/data-science---bsc-hons",
        "catalogValues": [
          "nodejs-web"
        ]
      },
      {
        "name": "scikit-learn / TensorFlow / PyTorch",
        "category": "Data / analysis tools",
        "usage": "Data, Year 2-3 - machine learning",
        "source": "U. Kent Data Science BSc, kent.ac.uk/courses/undergraduate/4407/data-science",
        "catalogValues": [
          "pytorch-tensorflow"
        ]
      },
      {
        "name": "Excel",
        "category": "Data / analysis tools",
        "usage": "Data, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "Stata / SPSS",
        "category": "Data / analysis tools",
        "usage": "Data - statistics at some universities",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "stata",
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "LaTeX / Overleaf, Markdown",
        "category": "Writing & productivity",
        "usage": "Writing, Year 2-3",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "overleaf"
        ]
      },
      {
        "name": "Cloud (AWS/GCP/Azure), MongoDB, Airflow",
        "category": "Elective-only",
        "usage": "Elective - data engineering",
        "source": "London Metropolitan Data Science BSc, londonmet.ac.uk/courses/undergraduate/data-science---bsc-hons",
        "catalogValues": [
          "docker-desktop",
          "sql-databases"
        ]
      }
    ],
    "softwareValues": [
      "python",
      "jupyter-anaconda",
      "pytorch-tensorflow",
      "r-rstudio",
      "sql-databases",
      "git",
      "tableau-desktop",
      "power-bi-desktop",
      "hadoop-spark",
      "nodejs-web",
      "microsoft-excel",
      "stata",
      "ibm-spss-statistics",
      "overleaf",
      "docker-desktop"
    ]
  },
  {
    "value": "mathematics-statistics",
    "label": "Mathematics & Statistics",
    "summary": "Covers Mathematics, Maths & Statistics, Applied/Pure Mathematics and related courses. Selecting this degree pre-fills the software typically used on the course — including MATLAB, Python, Maple — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Mathematics; Maths & Statistics; Applied/Pure Mathematics; Mathematics with Economics/Finance; Actuarial Science (LSE)",
    "universities": "All 20",
    "software": [
      {
        "name": "MATLAB",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - computational maths",
        "source": "U. Essex Mathematics BSc, essex.ac.uk/courses/ug00269/1/bsc-mathematics",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "Python",
        "category": "Core tools",
        "usage": "Core, increasingly Year 1 - numerical methods",
        "source": "U. Cambridge Faculty of Mathematics Computing, maths.cam.ac.uk/computing/software/maths",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "R / RStudio",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - statistics",
        "source": "U. Essex Mathematics BSc, essex.ac.uk/courses/ug00269/1/bsc-mathematics",
        "catalogValues": [
          "r-rstudio"
        ]
      },
      {
        "name": "LaTeX / Overleaf",
        "category": "Core tools",
        "usage": "Core, from Year 1 - writing mathematics",
        "source": "U. Cambridge Faculty of Mathematics Computing, maths.cam.ac.uk/computing/software/maths",
        "catalogValues": [
          "overleaf"
        ]
      },
      {
        "name": "Maple",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 1-2 - computer algebra",
        "source": "U. Cambridge Faculty of Mathematics, Maple, maths.cam.ac.uk/computing/computing/software/maple",
        "catalogValues": [
          "maple"
        ]
      },
      {
        "name": "Mathematica",
        "category": "Specialist / field-specific",
        "usage": "Specialist - symbolic computation",
        "source": "U. Cambridge Faculty of Mathematics Computing, maths.cam.ac.uk/computing/software/maths",
        "catalogValues": [
          "mathematica"
        ]
      },
      {
        "name": "Minitab",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 1 - introductory statistics",
        "source": "U. Cambridge Faculty of Mathematics Computing, maths.cam.ac.uk/computing/software/maths",
        "catalogValues": [
          "minitab"
        ]
      },
      {
        "name": "SPSS",
        "category": "Data / analysis tools",
        "usage": "Data - service/statistics courses",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "Excel",
        "category": "Data / analysis tools",
        "usage": "Data, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Writing & productivity",
        "usage": "Writing, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "SageMath, GAP, Stata, C++",
        "category": "Elective-only",
        "usage": "Elective - pure/applied & numerical",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "stata",
          "c-cpp-toolchain"
        ]
      }
    ],
    "softwareValues": [
      "matlab-simulink",
      "python",
      "r-rstudio",
      "overleaf",
      "maple",
      "mathematica",
      "minitab",
      "ibm-spss-statistics",
      "microsoft-excel",
      "microsoft-365",
      "stata",
      "c-cpp-toolchain"
    ]
  },
  {
    "value": "physics-astronomy",
    "label": "Physics & Astronomy",
    "summary": "Covers Physics, Physics with Astrophysics/Astronomy, Theoretical Physics and related courses. Selecting this degree pre-fills the software typically used on the course — including Python, MATLAB, LabVIEW — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Physics; Physics with Astrophysics/Astronomy; Theoretical Physics; Mathematical Physics; Computational Physics; Natural Sciences (physics stream)",
    "universities": "All except LSE",
    "software": [
      {
        "name": "Python",
        "category": "Core tools",
        "usage": "Core, from Year 1 (with MATLAB at Oxford)",
        "source": "U. Oxford Dept of Physics UG FAQ, physics.ox.ac.uk/study/undergraduates/faq/faq-physics-courses",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "MATLAB",
        "category": "Core tools",
        "usage": "Core, from Year 1 - computing & analysis",
        "source": "U. Oxford Dept of Physics IT, MATLAB, www2.physics.ox.ac.uk/it-services/matlab-0",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "LaTeX / Overleaf",
        "category": "Core tools",
        "usage": "Core, from Year 1 - lab reports",
        "source": "The Student Room, thestudentroom.co.uk/showthread.php?t=2703162",
        "catalogValues": [
          "overleaf"
        ]
      },
      {
        "name": "Excel / Origin",
        "category": "Core tools",
        "usage": "Core, from Year 1 - lab data",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel",
          "surfer-grapher"
        ]
      },
      {
        "name": "LabVIEW",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - instrumentation labs",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "labview"
        ]
      },
      {
        "name": "OriginLab (Origin)",
        "category": "Specialist / field-specific",
        "usage": "Specialist - curve fitting & plotting",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "surfer-grapher"
        ]
      },
      {
        "name": "C / C++ / Fortran",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - computational physics",
        "source": "Loughborough Physics with Computing MPhys, lboro.ac.uk/study/undergraduate/courses/physics-with-computing-mphys",
        "catalogValues": [
          "c-cpp-toolchain"
        ]
      },
      {
        "name": "ROOT",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - particle/nuclear",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "c-cpp-toolchain"
        ]
      },
      {
        "name": "Python (NumPy/SciPy/Matplotlib), Jupyter",
        "category": "Data / analysis tools",
        "usage": "Data, Year 2 - analysis & simulation",
        "source": "U. Oxford Dept of Physics UG FAQ, physics.ox.ac.uk/study/undergraduates/faq/faq-physics-courses",
        "catalogValues": [
          "python",
          "jupyter-anaconda"
        ]
      },
      {
        "name": "LaTeX / Overleaf, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing, throughout",
        "source": "The Student Room, thestudentroom.co.uk/showthread.php?t=2703162",
        "catalogValues": [
          "overleaf",
          "microsoft-365"
        ]
      },
      {
        "name": "Astropy/DS9/IRAF (astro); CUDA/HPC (computational)",
        "category": "Elective-only",
        "usage": "Elective - astrophysics & computational",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "jupyter-anaconda",
          "pytorch-tensorflow"
        ]
      }
    ],
    "softwareValues": [
      "python",
      "matlab-simulink",
      "overleaf",
      "microsoft-excel",
      "surfer-grapher",
      "labview",
      "c-cpp-toolchain",
      "jupyter-anaconda",
      "microsoft-365",
      "pytorch-tensorflow"
    ]
  },
  {
    "value": "chemistry",
    "label": "Chemistry",
    "summary": "Covers Chemistry, Medicinal/Pharmaceutical Chemistry, Chemistry with a Year Abroad/Industry and related courses. Selecting this degree pre-fills the software typically used on the course — including ChemDraw (ChemOffice), Excel / Origin, SciFinder / Reaxys — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Chemistry; Medicinal/Pharmaceutical Chemistry; Chemistry with a Year Abroad/Industry; Natural Sciences (chemistry stream)",
    "universities": "All except LSE",
    "software": [
      {
        "name": "ChemDraw (ChemOffice)",
        "category": "Core tools",
        "usage": "Core, from Year 1 - structures & mechanisms",
        "source": "University library guides (Stanford/Lehigh) on ChemDraw + SciFinder in UG chemistry",
        "catalogValues": [
          "chemdraw"
        ]
      },
      {
        "name": "Excel / Origin",
        "category": "Core tools",
        "usage": "Core, from Year 1 - lab data",
        "source": "U. Edinburgh School of Chemistry Computing & Software, chem.ed.ac.uk/cto/student-support/computing-software",
        "catalogValues": [
          "microsoft-excel",
          "surfer-grapher"
        ]
      },
      {
        "name": "MestReNova (Mnova)",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - NMR & spectral processing",
        "source": "Mnova (Mestrelab) NMR/LC-GC-MS processing - standard UG spectroscopy tool",
        "catalogValues": [
          "mestrenova"
        ]
      },
      {
        "name": "SciFinder / Reaxys",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 1-2 - literature & reaction search",
        "source": "University library guides (Stanford/Lehigh) on ChemDraw + SciFinder",
        "catalogValues": []
      },
      {
        "name": "Gaussian / GaussView",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 3+ - computational chemistry",
        "source": "U. Edinburgh School of Chemistry Computing & Software, chem.ed.ac.uk/cto/student-support/computing-software",
        "catalogValues": [
          "gaussian-gaussview"
        ]
      },
      {
        "name": "Avogadro / Spartan",
        "category": "Specialist / field-specific",
        "usage": "Specialist - molecular modelling",
        "source": "U. Edinburgh School of Chemistry Computing & Software, chem.ed.ac.uk/cto/student-support/computing-software",
        "catalogValues": [
          "avogadro-spartan"
        ]
      },
      {
        "name": "Mercury / CCDC",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - crystallography",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "mercury-ccdc"
        ]
      },
      {
        "name": "Origin, Excel, Python/MATLAB",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "U. Edinburgh School of Chemistry Computing & Software, chem.ed.ac.uk/cto/student-support/computing-software",
        "catalogValues": [
          "surfer-grapher",
          "microsoft-excel",
          "python",
          "matlab-simulink"
        ]
      },
      {
        "name": "EndNote / Mendeley, MS Word",
        "category": "Writing & productivity",
        "usage": "Writing - referencing & reports",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-word"
        ]
      },
      {
        "name": "ORCA, VMD, AutoDock",
        "category": "Elective-only",
        "usage": "Elective - computational/medicinal chemistry",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "orca",
          "vmd"
        ]
      }
    ],
    "softwareValues": [
      "chemdraw",
      "microsoft-excel",
      "surfer-grapher",
      "mestrenova",
      "gaussian-gaussview",
      "avogadro-spartan",
      "mercury-ccdc",
      "python",
      "matlab-simulink",
      "zotero",
      "microsoft-word",
      "orca",
      "vmd"
    ]
  },
  {
    "value": "biological-biomedical-sciences",
    "label": "Biological & Biomedical Sciences",
    "summary": "Covers Biology, Biological Sciences, Biochemistry and related courses. Selecting this degree pre-fills the software typically used on the course — including Excel, SPSS or R / RStudio, SnapGene / Benchling — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Biology; Biological Sciences; Biochemistry; Biomedical Science; Zoology; Genetics; Microbiology; Molecular Biology; Neuroscience; Physiology; Biotechnology",
    "universities": "All except LSE",
    "software": [
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1 - data recording",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "SPSS or R / RStudio",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - statistics for biologists",
        "source": "GraphPad Prism features (life-science statistics), graphpad.com/features",
        "catalogValues": [
          "ibm-spss-statistics",
          "r-rstudio"
        ]
      },
      {
        "name": "GraphPad Prism",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - statistics & graphing",
        "source": "GraphPad Prism features (life-science statistics), graphpad.com/features",
        "catalogValues": [
          "graphpad-prism"
        ]
      },
      {
        "name": "ImageJ / Fiji",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - microscopy & image analysis",
        "source": "Bioinformatics tool lists (BLAST, Geneious, SnapGene, Galaxy, Benchling)",
        "catalogValues": [
          "imagej-fiji"
        ]
      },
      {
        "name": "SnapGene / Benchling",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - molecular cloning",
        "source": "Bioinformatics tool lists; SnapGene product docs",
        "catalogValues": [
          "snapgene"
        ]
      },
      {
        "name": "BLAST & bioinformatics (Geneious, Galaxy)",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - sequence analysis",
        "source": "Bioinformatics tool lists (BLAST, Geneious, Galaxy)",
        "catalogValues": [
          "geneious"
        ]
      },
      {
        "name": "Bioconductor (R)",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - genomics",
        "source": "Bioinformatics tool lists",
        "catalogValues": [
          "r-rstudio"
        ]
      },
      {
        "name": "R / Python",
        "category": "Data / analysis tools",
        "usage": "Data, Year 2-3 - statistical & omics",
        "source": "GraphPad Prism features, graphpad.com/features",
        "catalogValues": [
          "r-rstudio",
          "python"
        ]
      },
      {
        "name": "EndNote / Mendeley / Zotero, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "MATLAB/Psychtoolbox (neuro); QGIS & R-vegan (ecology)",
        "category": "Elective-only",
        "usage": "Elective - stream-specific",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "matlab-simulink",
          "qgis"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-excel",
      "ibm-spss-statistics",
      "r-rstudio",
      "graphpad-prism",
      "imagej-fiji",
      "snapgene",
      "geneious",
      "python",
      "zotero",
      "microsoft-365",
      "matlab-simulink",
      "qgis"
    ]
  },
  {
    "value": "earth-environmental-sciences",
    "label": "Earth & Environmental Sciences",
    "summary": "Covers Geology, Earth Sciences, Environmental Science and related courses. Selecting this degree pre-fills the software typically used on the course — including GIS - ArcGIS / ArcGIS Pro, QGIS, Remote sensing - ENVI/ERDAS/Google Earth Engine — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Geology; Earth Sciences; Environmental Science; Geophysics; Geoscience; Sustainable Development (St Andrews)",
    "universities": "Imperial, St Andrews, Loughborough, Bath + broad-14 subset. Not LSE",
    "software": [
      {
        "name": "GIS - ArcGIS / ArcGIS Pro",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - spatial analysis",
        "source": "U. Edinburgh School of GeoSciences GIS suite, geosciences.ed.ac.uk/about/facilities/all/gis",
        "catalogValues": [
          "arcgis-pro"
        ]
      },
      {
        "name": "QGIS",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - open-source GIS",
        "source": "Cardiff Earth & Environmental Sciences GIS course (QGIS)",
        "catalogValues": [
          "qgis"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "R or Python",
        "category": "Core tools",
        "usage": "Core, Year 2 - quantitative analysis",
        "source": "U. Edinburgh School of GeoSciences GIS suite, geosciences.ed.ac.uk/about/facilities/all/gis",
        "catalogValues": [
          "r-rstudio",
          "python"
        ]
      },
      {
        "name": "Remote sensing - ENVI/ERDAS/Google Earth Engine",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - satellite/image analysis",
        "source": "U. Cambridge Dept of Geography Computing/GIS/RS, geog.cam.ac.uk/facilities/computing",
        "catalogValues": [
          "envi-erdas",
          "google-earth-engine"
        ]
      },
      {
        "name": "Petrel / Move / Leapfrog / Stereonet",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - geological modelling",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "petrel",
          "leapfrog"
        ]
      },
      {
        "name": "Surfer / Grapher (Golden Software)",
        "category": "Specialist / field-specific",
        "usage": "Specialist - contouring & visualisation",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "surfer-grapher"
        ]
      },
      {
        "name": "MATLAB",
        "category": "Data / analysis tools",
        "usage": "Data - geophysics & modelling",
        "source": "U. Cambridge Dept of Geography Computing, geog.cam.ac.uk/facilities/computing",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "EndNote / Mendeley, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "Seismic Unix, Oasis Montaj; climate/hydrology models",
        "category": "Elective-only",
        "usage": "Elective - geophysics & environmental modelling",
        "source": "Industry-standard tool for the field",
        "catalogValues": []
      }
    ],
    "softwareValues": [
      "arcgis-pro",
      "qgis",
      "microsoft-excel",
      "r-rstudio",
      "python",
      "envi-erdas",
      "google-earth-engine",
      "petrel",
      "leapfrog",
      "surfer-grapher",
      "matlab-simulink",
      "zotero",
      "microsoft-365"
    ]
  },
  {
    "value": "mechanical-aerospace-automotive-engineering",
    "label": "Mechanical, Aerospace & Automotive Engineering",
    "summary": "Covers Mechanical Eng, Aeronautical/Aerospace Eng, Automotive Eng and related courses. Selecting this degree pre-fills the software typically used on the course — including MATLAB & Simulink, SolidWorks, ANSYS (Mechanical / Fluent) — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Mechanical Eng; Aeronautical/Aerospace Eng; Automotive Eng; General/Integrated Eng; Manufacturing Eng; Robotics/Mechatronics; Materials Eng",
    "universities": "Imperial, Loughborough, Bath, Surrey + broad-14 (most). Not LSE, St Andrews",
    "software": [
      {
        "name": "MATLAB & Simulink",
        "category": "Core tools",
        "usage": "Core, from Year 1 - modelling, control, analysis",
        "source": "Queen's U. Belfast EEECS engineering software list, selfservice.eeecs.qub.ac.uk/engineeringlist.php",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "SolidWorks",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - 3D CAD",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "solidworks"
        ]
      },
      {
        "name": "AutoCAD",
        "category": "Core tools",
        "usage": "Core, Year 1 - 2D drafting",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "autocad"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1 - data analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "ANSYS (Mechanical / Fluent)",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - FEA & CFD",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ansys-workbench"
        ]
      },
      {
        "name": "CATIA",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - aerospace/automotive CAD",
        "source": "Queen's U. Belfast EEECS engineering software list, selfservice.eeecs.qub.ac.uk/engineeringlist.php",
        "catalogValues": [
          "catia"
        ]
      },
      {
        "name": "Siemens NX / PTC Creo / Autodesk Inventor",
        "category": "Specialist / field-specific",
        "usage": "Specialist - CAD alternatives",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "siemens-nx",
          "ptc-creo",
          "autodesk-inventor"
        ]
      },
      {
        "name": "OpenFOAM",
        "category": "Specialist / field-specific",
        "usage": "Specialist - open-source CFD",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "openfoam"
        ]
      },
      {
        "name": "Python",
        "category": "Data / analysis tools",
        "usage": "Data - increasingly for analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "LabVIEW",
        "category": "Data / analysis tools",
        "usage": "Data - instrumentation & test",
        "source": "U. Cambridge Dept of Engineering computing help, help.eng.cam.ac.uk/software/comsol",
        "catalogValues": [
          "labview"
        ]
      },
      {
        "name": "LaTeX / MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - reports",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "overleaf",
          "microsoft-365"
        ]
      },
      {
        "name": "Abaqus, Adams, GT-Suite, Fusion 360 (CAM)",
        "category": "Elective-only",
        "usage": "Elective - advanced FEA/multibody/CAM",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "abaqus",
          "fusion-360"
        ]
      }
    ],
    "softwareValues": [
      "matlab-simulink",
      "solidworks",
      "autocad",
      "microsoft-excel",
      "ansys-workbench",
      "catia",
      "siemens-nx",
      "ptc-creo",
      "autodesk-inventor",
      "openfoam",
      "python",
      "labview",
      "overleaf",
      "microsoft-365",
      "abaqus",
      "fusion-360"
    ]
  },
  {
    "value": "civil-structural-engineering",
    "label": "Civil & Structural Engineering",
    "summary": "Covers Civil Eng, Structural Eng, Architectural Engineering and related courses. Selecting this degree pre-fills the software typically used on the course — including AutoCAD, Revit, SAP2000 / ETABS — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Civil Eng; Structural Eng; Architectural Engineering; Civil & Environmental Eng",
    "universities": "Imperial, Loughborough, Bath, Surrey + broad-14 offering civil (UCL, Birmingham, Bristol, Sheffield, Southampton, Warwick, Exeter, Durham, Edinburgh). Not LSE, St Andrews",
    "software": [
      {
        "name": "AutoCAD",
        "category": "Core tools",
        "usage": "Core, Year 1 - drafting",
        "source": "Complete University Guide, 'How to become a structural engineer', thecompleteuniversityguide.co.uk/student-advice/careers/how-to-become-a-structural-engineer",
        "catalogValues": [
          "autocad"
        ]
      },
      {
        "name": "Revit",
        "category": "Core tools",
        "usage": "Core, Year 2 - BIM",
        "source": "Complete University Guide, 'How to become a structural engineer', thecompleteuniversityguide.co.uk/student-advice/careers/how-to-become-a-structural-engineer",
        "catalogValues": [
          "autodesk-revit"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "MATLAB",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - analysis",
        "source": "Queen's U. Belfast EEECS engineering software list, selfservice.eeecs.qub.ac.uk/engineeringlist.php",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "SAP2000 / ETABS",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - structural analysis",
        "source": "Complete University Guide, 'How to become a structural engineer', thecompleteuniversityguide.co.uk",
        "catalogValues": [
          "sap2000-etabs"
        ]
      },
      {
        "name": "Autodesk Robot / LUSAS",
        "category": "Specialist / field-specific",
        "usage": "Specialist - structural analysis alternatives",
        "source": "Complete University Guide, 'How to become a structural engineer', thecompleteuniversityguide.co.uk",
        "catalogValues": [
          "autodesk-robot-lusas"
        ]
      },
      {
        "name": "Tekla Structures",
        "category": "Specialist / field-specific",
        "usage": "Specialist - BIM detailing",
        "source": "Complete University Guide, 'How to become a structural engineer', thecompleteuniversityguide.co.uk",
        "catalogValues": [
          "tekla-structures"
        ]
      },
      {
        "name": "Civil 3D",
        "category": "Specialist / field-specific",
        "usage": "Specialist - infrastructure design",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "autocad-civil-3d"
        ]
      },
      {
        "name": "Python",
        "category": "Data / analysis tools",
        "usage": "Data - increasingly",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "MS Office, LaTeX",
        "category": "Writing & productivity",
        "usage": "Writing - reports",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365",
          "overleaf"
        ]
      },
      {
        "name": "PLAXIS (geotech), HEC-RAS (hydraulics), OpenRoads",
        "category": "Elective-only",
        "usage": "Elective - geotechnical/hydraulics/highways",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "plaxis"
        ]
      }
    ],
    "softwareValues": [
      "autocad",
      "autodesk-revit",
      "microsoft-excel",
      "matlab-simulink",
      "sap2000-etabs",
      "autodesk-robot-lusas",
      "tekla-structures",
      "autocad-civil-3d",
      "python",
      "microsoft-365",
      "overleaf",
      "plaxis"
    ]
  },
  {
    "value": "electrical-electronic-engineering",
    "label": "Electrical & Electronic Engineering",
    "summary": "Covers Electrical Eng, Electronic Eng, Electrical & Electronic Eng and related courses. Selecting this degree pre-fills the software typically used on the course — including MATLAB & Simulink, Multisim / OrCAD PSpice, Cadence — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Electrical Eng; Electronic Eng; Electrical & Electronic Eng; Communications/Systems Eng; some Robotics",
    "universities": "Imperial, Bath, Surrey, Loughborough + broad-14 (Southampton, Sheffield, Bristol, Birmingham, York, Edinburgh, UCL, KCL). Not LSE, St Andrews",
    "software": [
      {
        "name": "MATLAB & Simulink",
        "category": "Core tools",
        "usage": "Core, from Year 1 - signals & systems",
        "source": "Free Open Source Software in Electronics Engineering Education: A Survey, mecs-press.org",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "Multisim / OrCAD PSpice",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - circuit simulation",
        "source": "Free Open Source Software in Electronics Engineering Education: A Survey, mecs-press.org",
        "catalogValues": [
          "ni-multisim",
          "orcad-pspice"
        ]
      },
      {
        "name": "C / embedded programming",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - microcontrollers",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "c-cpp-toolchain"
        ]
      },
      {
        "name": "Cadence",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - IC design",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "cadence-virtuoso"
        ]
      },
      {
        "name": "Altium Designer / KiCad",
        "category": "Specialist / field-specific",
        "usage": "Specialist - PCB design",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "altium-designer",
          "kicad"
        ]
      },
      {
        "name": "LabVIEW",
        "category": "Specialist / field-specific",
        "usage": "Specialist - instrumentation & measurement",
        "source": "Free Open Source Software in Electronics Engineering Education: A Survey, mecs-press.org",
        "catalogValues": [
          "labview"
        ]
      },
      {
        "name": "VHDL/Verilog (Vivado/Quartus/ModelSim)",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - digital/FPGA design",
        "source": "Free Open Source Software in Electronics Engineering Education: A Survey, mecs-press.org",
        "catalogValues": [
          "fpga-vivado"
        ]
      },
      {
        "name": "Python",
        "category": "Data / analysis tools",
        "usage": "Data - analysis & DSP",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "LaTeX / MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - reports",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "overleaf",
          "microsoft-365"
        ]
      },
      {
        "name": "LTspice, ADS, Keil, Xilinx Vivado",
        "category": "Elective-only",
        "usage": "Elective - RF/embedded/advanced design",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ltspice",
          "fpga-vivado"
        ]
      }
    ],
    "softwareValues": [
      "matlab-simulink",
      "ni-multisim",
      "orcad-pspice",
      "c-cpp-toolchain",
      "cadence-virtuoso",
      "altium-designer",
      "kicad",
      "labview",
      "fpga-vivado",
      "python",
      "overleaf",
      "microsoft-365",
      "ltspice"
    ]
  },
  {
    "value": "chemical-process-engineering",
    "label": "Chemical & Process Engineering",
    "summary": "Covers Chemical Eng, Chemical & Biochemical Eng, Process Eng. Selecting this degree pre-fills the software typically used on the course — including Aspen Plus, MATLAB, Aspen HYSYS — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Chemical Eng; Chemical & Biochemical Eng; Process Eng",
    "universities": "Imperial, Bath, Surrey, Loughborough + broad-14 (UCL, Birmingham, Sheffield, Edinburgh, Cambridge via general). Not LSE, St Andrews",
    "software": [
      {
        "name": "Aspen Plus",
        "category": "Core tools",
        "usage": "Core, Year 2 - process simulation",
        "source": "U. Manchester ChemEng Aspen simulation workshops, tandfonline.com/doi/full/10.1080/10494820.2025.2529445",
        "catalogValues": [
          "aspen-plus"
        ]
      },
      {
        "name": "MATLAB",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - modelling",
        "source": "Queen's U. Belfast EEECS engineering software list, selfservice.eeecs.qub.ac.uk/engineeringlist.php",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "Aspen HYSYS",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - process simulation",
        "source": "U. Manchester ChemEng Aspen simulation workshops, tandfonline.com/doi/full/10.1080/10494820.2025.2529445",
        "catalogValues": [
          "aspen-hysys"
        ]
      },
      {
        "name": "gPROMS",
        "category": "Specialist / field-specific",
        "usage": "Specialist - dynamic process modelling",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "gproms"
        ]
      },
      {
        "name": "UniSim / PRO-II / CHEMCAD",
        "category": "Specialist / field-specific",
        "usage": "Specialist - simulation alternatives",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "aspen-hysys"
        ]
      },
      {
        "name": "COMSOL Multiphysics",
        "category": "Specialist / field-specific",
        "usage": "Specialist - transport phenomena",
        "source": "U. Cambridge Dept of Engineering computing help, help.eng.cam.ac.uk/software/comsol",
        "catalogValues": [
          "comsol-multiphysics"
        ]
      },
      {
        "name": "Python",
        "category": "Data / analysis tools",
        "usage": "Data - increasingly",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "MS Office, LaTeX",
        "category": "Writing & productivity",
        "usage": "Writing - reports",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365",
          "overleaf"
        ]
      },
      {
        "name": "Aspen Dynamics, DWSIM",
        "category": "Elective-only",
        "usage": "Elective - dynamics/open-source simulation",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "aspen-plus"
        ]
      }
    ],
    "softwareValues": [
      "aspen-plus",
      "matlab-simulink",
      "microsoft-excel",
      "aspen-hysys",
      "gproms",
      "comsol-multiphysics",
      "python",
      "microsoft-365",
      "overleaf"
    ]
  },
  {
    "value": "biomedical-engineering",
    "label": "Biomedical Engineering",
    "summary": "Covers Biomedical Eng, Medical Eng, Bioengineering. Selecting this degree pre-fills the software typically used on the course — including MATLAB, SolidWorks, COMSOL Multiphysics — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Biomedical Eng; Medical Eng; Bioengineering",
    "universities": "Imperial, Loughborough, Surrey + broad-14 (KCL, Sheffield, Southampton, Birmingham, UCL, Oxford)",
    "software": [
      {
        "name": "MATLAB",
        "category": "Core tools",
        "usage": "Core, from Year 1 - signal/data analysis",
        "source": "U. Oxford Dept of Engineering Science software labs, eng.ox.ac.uk/virtual-tour/software-labs",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "SolidWorks",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - 3D design",
        "source": "U. Dundee Biomedical Engineering Design module BE21001, dundee.ac.uk/module/be21001",
        "catalogValues": [
          "solidworks"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "COMSOL Multiphysics",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - modelling & validation",
        "source": "UCL Medical Physics & Biomedical Engineering UG, ucl.ac.uk/medical-physics-biomedical-engineering",
        "catalogValues": [
          "comsol-multiphysics"
        ]
      },
      {
        "name": "LabVIEW",
        "category": "Specialist / field-specific",
        "usage": "Specialist - data acquisition",
        "source": "U. Cambridge Dept of Engineering computing help, help.eng.cam.ac.uk/software/comsol",
        "catalogValues": [
          "labview"
        ]
      },
      {
        "name": "ANSYS",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - FEA",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ansys-workbench"
        ]
      },
      {
        "name": "ImageJ / 3D Slicer",
        "category": "Specialist / field-specific",
        "usage": "Specialist - medical image analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "imagej-fiji",
          "3d-slicer"
        ]
      },
      {
        "name": "Python / R",
        "category": "Data / analysis tools",
        "usage": "Data - analysis & ML",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "python",
          "r-rstudio"
        ]
      },
      {
        "name": "LaTeX / MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - reports",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "overleaf",
          "microsoft-365"
        ]
      },
      {
        "name": "Mimics, Simulink, OpenSim",
        "category": "Elective-only",
        "usage": "Elective - imaging/biomechanics modelling",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "matlab-simulink"
        ]
      }
    ],
    "softwareValues": [
      "matlab-simulink",
      "solidworks",
      "microsoft-excel",
      "comsol-multiphysics",
      "labview",
      "ansys-workbench",
      "imagej-fiji",
      "3d-slicer",
      "python",
      "r-rstudio",
      "overleaf",
      "microsoft-365"
    ]
  },
  {
    "value": "architecture",
    "label": "Architecture",
    "summary": "Covers Architecture, Architectural Studies, Architecture & Environmental Design. Selecting this degree pre-fills the software typically used on the course — including AutoCAD, SketchUp, Rhino + Grasshopper — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Architecture; Architectural Studies; Architecture & Environmental Design",
    "universities": "UCL (Bartlett), Sheffield, Bath, Edinburgh, Cambridge, Loughborough. Not LSE, Imperial, St Andrews, Surrey",
    "software": [
      {
        "name": "AutoCAD",
        "category": "Core tools",
        "usage": "Core, Year 1 - drafting",
        "source": "UWE Architecture technical support, tslr.uwe.ac.uk/cate/architecture",
        "catalogValues": [
          "autocad"
        ]
      },
      {
        "name": "SketchUp",
        "category": "Core tools",
        "usage": "Core, Year 1 - quick 3D modelling",
        "source": "UWE Architecture technical support, tslr.uwe.ac.uk/cate/architecture",
        "catalogValues": [
          "sketchup"
        ]
      },
      {
        "name": "Revit",
        "category": "Core tools",
        "usage": "Core, Year 2 - BIM",
        "source": "UWE Architecture technical support, tslr.uwe.ac.uk/cate/architecture",
        "catalogValues": [
          "autodesk-revit"
        ]
      },
      {
        "name": "Adobe Creative Cloud (Photoshop, InDesign, Illustrator)",
        "category": "Core tools",
        "usage": "Core, from Year 1 - presentation & portfolio",
        "source": "Newcastle U. IT Service, Adobe Creative Cloud licence, services.ncl.ac.uk/itservice/core-services/software/licences/adobelicenses",
        "catalogValues": [
          "adobe-creative-cloud"
        ]
      },
      {
        "name": "Rhino + Grasshopper",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2-3 - parametric modelling",
        "source": "UWE Architecture technical support, tslr.uwe.ac.uk/cate/architecture",
        "catalogValues": [
          "rhino-3d"
        ]
      },
      {
        "name": "ArchiCAD",
        "category": "Specialist / field-specific",
        "usage": "Specialist - BIM alternative",
        "source": "UWE Architecture technical support, tslr.uwe.ac.uk/cate/architecture",
        "catalogValues": [
          "archicad"
        ]
      },
      {
        "name": "Enscape / Lumion / Twinmotion",
        "category": "Specialist / field-specific",
        "usage": "Specialist - real-time rendering",
        "source": "UWE Architecture technical support, tslr.uwe.ac.uk/cate/architecture",
        "catalogValues": [
          "enscape",
          "lumion",
          "twinmotion"
        ]
      },
      {
        "name": "V-Ray",
        "category": "Specialist / field-specific",
        "usage": "Specialist - photoreal rendering",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "v-ray"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Writing & productivity",
        "usage": "Writing - throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "Ladybug/Honeybee (environmental), 3ds Max",
        "category": "Elective-only",
        "usage": "Elective - environmental analysis & advanced viz",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "3ds-max"
        ]
      }
    ],
    "softwareValues": [
      "autocad",
      "sketchup",
      "autodesk-revit",
      "adobe-creative-cloud",
      "rhino-3d",
      "archicad",
      "enscape",
      "lumion",
      "twinmotion",
      "v-ray",
      "microsoft-365",
      "3ds-max"
    ]
  },
  {
    "value": "product-industrial-design-design-engineering",
    "label": "Product & Industrial Design / Design Engineering",
    "summary": "Covers Product Design, Industrial Design, Design Engineering and related courses. Selecting this degree pre-fills the software typically used on the course — including SolidWorks, Adobe Creative Cloud, Autodesk Fusion 360 — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Product Design; Industrial Design; Design Engineering; Integrated Design Engineering (Bath)",
    "universities": "Loughborough, Bath, Imperial (Design Eng) + broad-14 subset",
    "software": [
      {
        "name": "SolidWorks",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - CAD",
        "source": "Bangor U. Product Design (SolidWorks, Adobe, KeyShot), bangor.ac.uk/study/undergraduate/product-design",
        "catalogValues": [
          "solidworks"
        ]
      },
      {
        "name": "Adobe Creative Cloud",
        "category": "Core tools",
        "usage": "Core, from Year 1 - sketching & presentation",
        "source": "Bangor U. Product Design, bangor.ac.uk/study/undergraduate/product-design",
        "catalogValues": [
          "adobe-creative-cloud"
        ]
      },
      {
        "name": "KeyShot",
        "category": "Core tools",
        "usage": "Core, Year 2 - product rendering",
        "source": "Bangor U. Product Design, bangor.ac.uk/study/undergraduate/product-design",
        "catalogValues": [
          "keyshot"
        ]
      },
      {
        "name": "Autodesk Fusion 360",
        "category": "Specialist / field-specific",
        "usage": "Specialist - CAD/CAM",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "autodesk-fusion-360"
        ]
      },
      {
        "name": "Rhino",
        "category": "Specialist / field-specific",
        "usage": "Specialist - surface modelling",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "rhino-3d"
        ]
      },
      {
        "name": "Autodesk Inventor / PTC Creo",
        "category": "Specialist / field-specific",
        "usage": "Specialist - CAD alternatives",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "autodesk-inventor",
          "ptc-creo"
        ]
      },
      {
        "name": "Slicer software (Cura) for 3D printing",
        "category": "Specialist / field-specific",
        "usage": "Specialist - prototyping",
        "source": "Bangor U. Product Design, bangor.ac.uk/study/undergraduate/product-design",
        "catalogValues": [
          "fusion-360"
        ]
      },
      {
        "name": "Excel",
        "category": "Data / analysis tools",
        "usage": "Data - user research",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Writing & productivity",
        "usage": "Writing - throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "CATIA, Autodesk Alias, Unity",
        "category": "Elective-only",
        "usage": "Elective - automotive/interaction design",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "catia",
          "autodesk-alias",
          "unity"
        ]
      }
    ],
    "softwareValues": [
      "solidworks",
      "adobe-creative-cloud",
      "keyshot",
      "autodesk-fusion-360",
      "rhino-3d",
      "autodesk-inventor",
      "ptc-creo",
      "fusion-360",
      "microsoft-excel",
      "microsoft-365",
      "catia",
      "autodesk-alias",
      "unity"
    ]
  },
  {
    "value": "art-graphic-design-creative-arts",
    "label": "Art, Graphic Design & Creative Arts",
    "summary": "Covers Fine Art, Graphic Design, Illustration and related courses. Selecting this degree pre-fills the software typically used on the course — including Adobe Photoshop, Adobe Illustrator, Procreate — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Fine Art; Graphic Design; Illustration; Creative Arts; Fashion/Textiles",
    "universities": "UCL (Slade), Loughborough, Edinburgh (ECA), Southampton (Winchester School of Art)",
    "software": [
      {
        "name": "Adobe Photoshop",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Arden U. BA Graphic Design (Adobe Creative Cloud), arden.ac.uk/our-courses/undergraduate/graphic-design-degrees",
        "catalogValues": [
          "adobe-photoshop"
        ]
      },
      {
        "name": "Adobe Illustrator",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Arden U. BA Graphic Design, arden.ac.uk/our-courses/undergraduate/graphic-design-degrees",
        "catalogValues": [
          "adobe-illustrator"
        ]
      },
      {
        "name": "Adobe InDesign",
        "category": "Core tools",
        "usage": "Core, from Year 1 - layout",
        "source": "Arden U. BA Graphic Design, arden.ac.uk/our-courses/undergraduate/graphic-design-degrees",
        "catalogValues": [
          "adobe-indesign"
        ]
      },
      {
        "name": "Procreate",
        "category": "Specialist / field-specific",
        "usage": "Specialist - digital illustration (iPad)",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "procreate"
        ]
      },
      {
        "name": "Adobe After Effects",
        "category": "Specialist / field-specific",
        "usage": "Specialist - motion graphics",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "adobe-after-effects"
        ]
      },
      {
        "name": "Blender / Cinema 4D",
        "category": "Specialist / field-specific",
        "usage": "Specialist - 3D",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "blender",
          "cinema-4d"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Writing & productivity",
        "usage": "Writing - throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "Figma (UX), TouchDesigner, CLO3D (fashion)",
        "category": "Elective-only",
        "usage": "Elective - UX/interactive/fashion",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "figma",
          "touchdesigner",
          "clo3d"
        ]
      }
    ],
    "softwareValues": [
      "adobe-photoshop",
      "adobe-illustrator",
      "adobe-indesign",
      "procreate",
      "adobe-after-effects",
      "blender",
      "cinema-4d",
      "microsoft-365",
      "figma",
      "touchdesigner",
      "clo3d"
    ]
  },
  {
    "value": "film-media-communications",
    "label": "Film, Media & Communications",
    "summary": "Covers Film, Film & TV Production, Media & Communications and related courses. Selecting this degree pre-fills the software typically used on the course — including Adobe Premiere Pro, Adobe Photoshop, Adobe After Effects — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Film; Film & TV Production; Media & Communications; Digital Media; Journalism",
    "universities": "Loughborough, Surrey, St Andrews, Warwick, Exeter, York, KCL, Southampton, Lancaster",
    "software": [
      {
        "name": "Adobe Premiere Pro",
        "category": "Core tools",
        "usage": "Core, Year 1 - video editing",
        "source": "SAE UK Film Production degree, sae.edu/gbr/film-london",
        "catalogValues": [
          "adobe-premiere-pro"
        ]
      },
      {
        "name": "Adobe Photoshop",
        "category": "Core tools",
        "usage": "Core, Year 1",
        "source": "Newcastle U. IT Service, Adobe Creative Cloud licence, services.ncl.ac.uk/itservice",
        "catalogValues": [
          "adobe-photoshop"
        ]
      },
      {
        "name": "DaVinci Resolve",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - colour grading & editing",
        "source": "SAE UK Film Production degree, sae.edu/gbr/film-london",
        "catalogValues": [
          "davinci-resolve"
        ]
      },
      {
        "name": "Adobe After Effects",
        "category": "Specialist / field-specific",
        "usage": "Specialist - VFX & motion",
        "source": "SAE UK Film Production degree, sae.edu/gbr/film-london",
        "catalogValues": [
          "adobe-after-effects"
        ]
      },
      {
        "name": "Avid Media Composer",
        "category": "Specialist / field-specific",
        "usage": "Specialist - professional editing",
        "source": "SAE UK Film Production degree, sae.edu/gbr/film-london",
        "catalogValues": [
          "avid-media-composer"
        ]
      },
      {
        "name": "Pro Tools / Adobe Audition",
        "category": "Specialist / field-specific",
        "usage": "Specialist - sound & mixing",
        "source": "SAE UK Film Production degree, sae.edu/gbr/film-london",
        "catalogValues": [
          "pro-tools",
          "adobe-audition"
        ]
      },
      {
        "name": "Excel",
        "category": "Data / analysis tools",
        "usage": "Data - production planning",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "Final Draft (screenwriting), MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - scripts & docs",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "Final Cut Pro, Cinema 4D, Unreal Engine",
        "category": "Elective-only",
        "usage": "Elective - alt editing/3D/virtual production",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "final-cut-pro",
          "cinema-4d",
          "unreal-engine"
        ]
      }
    ],
    "softwareValues": [
      "adobe-premiere-pro",
      "adobe-photoshop",
      "davinci-resolve",
      "adobe-after-effects",
      "avid-media-composer",
      "pro-tools",
      "adobe-audition",
      "microsoft-excel",
      "microsoft-365",
      "final-cut-pro",
      "cinema-4d",
      "unreal-engine"
    ]
  },
  {
    "value": "music-sound",
    "label": "Music & Sound",
    "summary": "Covers Music, Music Technology, Music & Sound Recording/Tonmeister (Surrey) and related courses. Selecting this degree pre-fills the software typically used on the course — including Logic Pro, Pro Tools, Ableton Live — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Music; Music Technology; Music & Sound Recording/Tonmeister (Surrey); Composition",
    "universities": "Surrey, Southampton, Edinburgh, York, Bristol, Birmingham, KCL, Oxford, Cambridge, Durham",
    "software": [
      {
        "name": "Logic Pro",
        "category": "Core tools",
        "usage": "Core, Year 1 - DAW",
        "source": "London Met Music Technology & Production BSc, londonmet.ac.uk/courses/undergraduate/music-technology-and-production---bsc-hons",
        "catalogValues": [
          "logic-pro"
        ]
      },
      {
        "name": "Pro Tools",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - recording & mixing",
        "source": "U. Kent Music & Audio Technology equipment, kent.ac.uk/arts/subjects/music-audio-technology/equipment",
        "catalogValues": [
          "pro-tools"
        ]
      },
      {
        "name": "Sibelius (or Dorico)",
        "category": "Core tools",
        "usage": "Core, Year 1 - music notation",
        "source": "Keele U. Music facilities, keele.ac.uk/humanities-social-sciences/ourfacilities",
        "catalogValues": [
          "sibelius-dorico"
        ]
      },
      {
        "name": "Ableton Live",
        "category": "Specialist / field-specific",
        "usage": "Specialist - electronic music & performance",
        "source": "London Met Music Technology & Production BSc, londonmet.ac.uk/courses/undergraduate/music-technology-and-production---bsc-hons",
        "catalogValues": [
          "ableton-live"
        ]
      },
      {
        "name": "Max/MSP (Max for Live)",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - interactive audio",
        "source": "London Met Music Technology & Production BSc, londonmet.ac.uk/courses/undergraduate/music-technology-and-production---bsc-hons",
        "catalogValues": [
          "max-msp"
        ]
      },
      {
        "name": "Cubase / Reaper",
        "category": "Specialist / field-specific",
        "usage": "Specialist - DAW alternatives",
        "source": "Keele U. Music facilities, keele.ac.uk/humanities-social-sciences/ourfacilities",
        "catalogValues": [
          "cubase-reaper"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Writing & productivity",
        "usage": "Writing - throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "Kontakt, Reaktor, SuperCollider, Pure Data",
        "category": "Elective-only",
        "usage": "Elective - sampling/synthesis/coding",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "kontakt"
        ]
      }
    ],
    "softwareValues": [
      "logic-pro",
      "pro-tools",
      "sibelius-dorico",
      "ableton-live",
      "max-msp",
      "cubase-reaper",
      "microsoft-365",
      "kontakt"
    ]
  },
  {
    "value": "medicine-dentistry-veterinary-medicine",
    "label": "Medicine, Dentistry & Veterinary Medicine",
    "summary": "Covers Medicine (MBBS/MBChB/BMBS), Dentistry (BDS), Veterinary Medicine (BVMSci/VetMB). Selecting this degree pre-fills the software typically used on the course — including Anki, Complete Anatomy (3D4Medical), PassMedicine / Quesmed / Osmosis — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Medicine (MBBS/MBChB/BMBS); Dentistry (BDS); Veterinary Medicine (BVMSci/VetMB)",
    "universities": "Medicine: Cambridge, Oxford, Imperial, UCL, KCL, Edinburgh, Birmingham, Bristol, Sheffield, Southampton, Exeter, St Andrews, Lancaster, York. Dentistry: Birmingham, Bristol, Sheffield, KCL, Edinburgh. Vet: Surrey, Cambridge, Edinburgh, Bristol",
    "software": [
      {
        "name": "Anki",
        "category": "Core tools",
        "usage": "Core, from Year 1 - spaced-repetition revision",
        "source": "UCL Reflect, 'Best Apps for Pre-Clinical Medicine', reflect.ucl.ac.uk/argtm/2020/11/05/92",
        "catalogValues": [
          "anki"
        ]
      },
      {
        "name": "Complete Anatomy (3D4Medical)",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - 3D anatomy",
        "source": "Complete Anatomy for students (3D4Medical), 3d4medical.com/student",
        "catalogValues": [
          "complete-anatomy"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "U. St Andrews School of Medicine student blog, education.wp.st-andrews.ac.uk/2020/09/08/slbs-4",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "PassMedicine / Quesmed / Osmosis",
        "category": "Specialist / field-specific",
        "usage": "Specialist - question banks & revision",
        "source": "UCL Reflect, reflect.ucl.ac.uk/argtm/2020/11/05/92",
        "catalogValues": [
          "passmedicine-quesmed"
        ]
      },
      {
        "name": "Clinical e-portfolio (MyProgress/PebblePad)",
        "category": "Specialist / field-specific",
        "usage": "Specialist, clinical years - logbook",
        "source": "Industry-standard tool for the field",
        "catalogValues": []
      },
      {
        "name": "SPSS",
        "category": "Data / analysis tools",
        "usage": "Data - SSC/research project statistics",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "EndNote / Mendeley, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "R (intercalated BSc), dental CAD/CAM, BioDigital",
        "category": "Elective-only",
        "usage": "Elective - intercalation & specialty tools",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio"
        ]
      }
    ],
    "softwareValues": [
      "anki",
      "complete-anatomy",
      "microsoft-365",
      "passmedicine-quesmed",
      "ibm-spss-statistics",
      "zotero",
      "r-rstudio"
    ]
  },
  {
    "value": "nursing-midwifery-allied-health",
    "label": "Nursing, Midwifery & Allied Health",
    "summary": "Covers Nursing, Midwifery, Physiotherapy and related courses. Selecting this degree pre-fills the software typically used on the course — including Microsoft Office, Clinical e-portfolio (PebblePad / MyProgress), Simulation / VR clinical apps — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Nursing; Midwifery; Physiotherapy; Occupational Therapy; Radiography; Paramedic Science; Speech & Language Therapy",
    "universities": "Surrey, Edinburgh, Birmingham, Southampton, Bristol, Sheffield, KCL, Exeter, York, Lancaster",
    "software": [
      {
        "name": "Microsoft Office",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "Clinical e-portfolio (PebblePad / MyProgress)",
        "category": "Core tools",
        "usage": "Core, from Year 1 - practice competencies",
        "source": "Industry-standard tool for the field",
        "catalogValues": []
      },
      {
        "name": "Simulation / VR clinical apps",
        "category": "Specialist / field-specific",
        "usage": "Specialist - skills training",
        "source": "Industry-standard tool for the field",
        "catalogValues": []
      },
      {
        "name": "SPSS",
        "category": "Data / analysis tools",
        "usage": "Data - dissertation statistics",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "Excel",
        "category": "Data / analysis tools",
        "usage": "Data, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "EndNote / Mendeley / RefWorks, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "NVivo (qualitative dissertation); PACS (radiography)",
        "category": "Elective-only",
        "usage": "Elective - qualitative & imaging streams",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "nvivo"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-365",
      "ibm-spss-statistics",
      "microsoft-excel",
      "zotero",
      "nvivo"
    ]
  },
  {
    "value": "pharmacy-pharmacology",
    "label": "Pharmacy & Pharmacology",
    "summary": "Covers Pharmacy (MPharm), Pharmacology, Pharmacology & Toxicology. Selecting this degree pre-fills the software typically used on the course — including Microsoft Office, MedicinesComplete / BNF, SPSS or R — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Pharmacy (MPharm); Pharmacology; Pharmacology & Toxicology",
    "universities": "Bath, Birmingham, KCL, UCL, Surrey",
    "software": [
      {
        "name": "Microsoft Office",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "MedicinesComplete / BNF",
        "category": "Core tools",
        "usage": "Core, from Year 1 - drug reference",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "medicinescomplete-bnf"
        ]
      },
      {
        "name": "GraphPad Prism",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - pharmacology data & stats",
        "source": "GraphPad Prism features, graphpad.com/features",
        "catalogValues": [
          "graphpad-prism"
        ]
      },
      {
        "name": "SPSS or R",
        "category": "Specialist / field-specific",
        "usage": "Specialist - statistics",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics",
          "r-rstudio"
        ]
      },
      {
        "name": "Molecular modelling / ChemDraw",
        "category": "Specialist / field-specific",
        "usage": "Specialist - medicinal chemistry",
        "source": "University library guides on ChemDraw",
        "catalogValues": [
          "chemdraw"
        ]
      },
      {
        "name": "Prism, SPSS, Excel",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "GraphPad Prism features, graphpad.com/features",
        "catalogValues": [
          "graphpad-prism",
          "ibm-spss-statistics",
          "microsoft-excel"
        ]
      },
      {
        "name": "EndNote / Mendeley",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero"
        ]
      },
      {
        "name": "Pharmacokinetics software (e.g. WinNonlin)",
        "category": "Elective-only",
        "usage": "Elective - PK modelling",
        "source": "Industry-standard tool for the field",
        "catalogValues": []
      }
    ],
    "softwareValues": [
      "microsoft-365",
      "medicinescomplete-bnf",
      "graphpad-prism",
      "ibm-spss-statistics",
      "r-rstudio",
      "chemdraw",
      "microsoft-excel",
      "zotero"
    ]
  },
  {
    "value": "psychology",
    "label": "Psychology",
    "summary": "Covers Psychology (BSc/BA), Cognitive Neuroscience, Behavioural Science and related courses. Selecting this degree pre-fills the software typically used on the course — including SPSS, Excel, JASP — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Psychology (BSc/BA); Cognitive Neuroscience; Behavioural Science; Psychological & Behavioural Science (LSE)",
    "universities": "All except Imperial (LSE via PBS; broad-14; St Andrews, Loughborough, Bath, Surrey)",
    "software": [
      {
        "name": "SPSS",
        "category": "Core tools",
        "usage": "Core, from Year 1 - statistics (dominant)",
        "source": "Statistics Education in UG Psychology: A Survey of UK Curricula, Collabra Psychology, online.ucpress.edu/collabra/article/8/1/38037",
        "catalogValues": [
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Statistics Education in UG Psychology, Collabra Psychology, online.ucpress.edu/collabra/article/8/1/38037",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "Qualtrics",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - survey building",
        "source": "Computing for Psychologists - software, computingforpsychologists.wordpress.com/category/software",
        "catalogValues": [
          "qualtrics"
        ]
      },
      {
        "name": "JASP",
        "category": "Specialist / field-specific",
        "usage": "Specialist - free stats alternative",
        "source": "Simply Put Psych, 'SPSS vs JASP', simplyputpsych.co.uk",
        "catalogValues": [
          "jasp"
        ]
      },
      {
        "name": "R / RStudio",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - advanced statistics",
        "source": "Statistics Education in UG Psychology, online.ucpress.edu/collabra/article/8/1/38037",
        "catalogValues": [
          "r-rstudio"
        ]
      },
      {
        "name": "PsychoPy",
        "category": "Specialist / field-specific",
        "usage": "Specialist - experiment building",
        "source": "Computing for Psychologists, computingforpsychologists.wordpress.com/category/software",
        "catalogValues": [
          "psychopy"
        ]
      },
      {
        "name": "E-Prime",
        "category": "Specialist / field-specific",
        "usage": "Specialist - stimulus presentation",
        "source": "Computing for Psychologists, computingforpsychologists.wordpress.com/category/software",
        "catalogValues": [
          "e-prime"
        ]
      },
      {
        "name": "SPSS, R, JASP",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Statistics Education in UG Psychology, online.ucpress.edu/collabra/article/8/1/38037",
        "catalogValues": [
          "ibm-spss-statistics",
          "r-rstudio",
          "jasp"
        ]
      },
      {
        "name": "EndNote / Mendeley / Zotero, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "NVivo (qualitative); MATLAB/Psychtoolbox (neuro); OpenSesame",
        "category": "Elective-only",
        "usage": "Elective - qualitative & neuroscience",
        "source": "Computing for Psychologists, computingforpsychologists.wordpress.com/category/software",
        "catalogValues": [
          "nvivo",
          "matlab-simulink"
        ]
      }
    ],
    "softwareValues": [
      "ibm-spss-statistics",
      "microsoft-excel",
      "qualtrics",
      "jasp",
      "r-rstudio",
      "psychopy",
      "e-prime",
      "zotero",
      "microsoft-365",
      "nvivo",
      "matlab-simulink"
    ]
  },
  {
    "value": "sport-exercise-science",
    "label": "Sport & Exercise Science",
    "summary": "Covers Sport & Exercise Science, Sport Science, Sports Technology and related courses. Selecting this degree pre-fills the software typically used on the course — including SPSS, Excel, Dartfish — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Sport & Exercise Science; Sport Science; Sports Technology; Strength & Conditioning",
    "universities": "Loughborough, Bath, Surrey, Birmingham, Exeter, Durham, Edinburgh",
    "software": [
      {
        "name": "SPSS",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - statistics",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "Kinovea",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - video/motion analysis (free)",
        "source": "Kinovea gait/motion analysis validation study, pmc.ncbi.nlm.nih.gov/articles/PMC7308968",
        "catalogValues": [
          "kinovea"
        ]
      },
      {
        "name": "Dartfish",
        "category": "Specialist / field-specific",
        "usage": "Specialist - performance analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "dartfish"
        ]
      },
      {
        "name": "Quintic",
        "category": "Specialist / field-specific",
        "usage": "Specialist - biomechanics video analysis",
        "source": "Quintic Sports biomechanics software (UG site licences), quinticsports.com/software",
        "catalogValues": [
          "quintic"
        ]
      },
      {
        "name": "Vicon Nexus",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - 3D motion capture (labs)",
        "source": "Loughborough Sport Biomechanics (Vicon, EMG), lboro.ac.uk/study/postgraduate/masters-degrees/a-z/sport-biomechanics",
        "catalogValues": [
          "vicon-nexus"
        ]
      },
      {
        "name": "SPSS, R, Excel",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics",
          "r-rstudio",
          "microsoft-excel"
        ]
      },
      {
        "name": "EndNote / Mendeley, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "MATLAB (biomechanics); Hudl/Sportscode; EMG software",
        "category": "Elective-only",
        "usage": "Elective - advanced biomechanics & notational analysis",
        "source": "Loughborough Sport Biomechanics, lboro.ac.uk/study/postgraduate/masters-degrees/a-z/sport-biomechanics",
        "catalogValues": [
          "matlab-simulink"
        ]
      }
    ],
    "softwareValues": [
      "ibm-spss-statistics",
      "microsoft-excel",
      "kinovea",
      "dartfish",
      "quintic",
      "vicon-nexus",
      "r-rstudio",
      "zotero",
      "microsoft-365",
      "matlab-simulink"
    ]
  },
  {
    "value": "economics",
    "label": "Economics",
    "summary": "Covers Economics, Econometrics & Mathematical Economics, Economics & Management and related courses. Selecting this degree pre-fills the software typically used on the course — including Stata, Excel, EViews — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Economics; Econometrics & Mathematical Economics; Economics & Management; PPE (quant side); Land Economy (Cambridge)",
    "universities": "LSE + broad-14 + St Andrews, Loughborough, Bath, Surrey",
    "software": [
      {
        "name": "Stata",
        "category": "Core tools",
        "usage": "Core, Year 2 - econometrics",
        "source": "U. Kent Introduction to Econometrics module EC580, kent.ac.uk/courses/modules/module/EC580",
        "catalogValues": [
          "stata"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "U. Kent Introduction to Econometrics module EC580, kent.ac.uk/courses/modules/module/EC580",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "R",
        "category": "Core tools",
        "usage": "Core, increasingly - statistics & econometrics",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio"
        ]
      },
      {
        "name": "EViews",
        "category": "Specialist / field-specific",
        "usage": "Specialist - time-series econometrics",
        "source": "Cardiff U. Econometrics module BS3551 (EViews), data.cardiff.ac.uk",
        "catalogValues": [
          "eviews"
        ]
      },
      {
        "name": "MATLAB",
        "category": "Specialist / field-specific",
        "usage": "Specialist - macro & quantitative",
        "source": "Queen Mary U. London Economics (Stata, MATLAB, Mathematica), qmul.ac.uk/undergraduate/coursefinder",
        "catalogValues": [
          "matlab-simulink"
        ]
      },
      {
        "name": "Python",
        "category": "Specialist / field-specific",
        "usage": "Specialist - increasingly for data",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "Stata, R, EViews, Excel",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "U. Kent Econometrics module EC580, kent.ac.uk/courses/modules/module/EC580",
        "catalogValues": [
          "stata",
          "r-rstudio",
          "eviews",
          "microsoft-excel"
        ]
      },
      {
        "name": "LaTeX, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - reports",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "overleaf",
          "microsoft-365"
        ]
      },
      {
        "name": "Mathematica, OxMetrics, SAS, Gretl",
        "category": "Elective-only",
        "usage": "Elective - advanced/alternative packages",
        "source": "Queen Mary U. London Economics, qmul.ac.uk/undergraduate/coursefinder",
        "catalogValues": [
          "mathematica",
          "sas"
        ]
      }
    ],
    "softwareValues": [
      "stata",
      "microsoft-excel",
      "r-rstudio",
      "eviews",
      "matlab-simulink",
      "python",
      "overleaf",
      "microsoft-365",
      "mathematica",
      "sas"
    ]
  },
  {
    "value": "business-management-marketing",
    "label": "Business, Management & Marketing",
    "summary": "Covers Business, Management, Business Management and related courses. Selecting this degree pre-fills the software typically used on the course — including Microsoft Excel, PowerPoint, Tableau / Power BI — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Business; Management; Business Management; Marketing; International Business",
    "universities": "LSE, Imperial (Business School BSc), broad-14 (Oxford/Cambridge only via Economics & Management), Loughborough, Bath, Surrey, St Andrews",
    "software": [
      {
        "name": "Microsoft Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1 - analysis & modelling",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "PowerPoint",
        "category": "Core tools",
        "usage": "Core, throughout - presentations",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-powerpoint"
        ]
      },
      {
        "name": "SPSS",
        "category": "Core tools",
        "usage": "Core, Year 2 - research methods",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "Tableau / Power BI",
        "category": "Specialist / field-specific",
        "usage": "Specialist - business analytics",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "tableau-desktop",
          "power-bi-desktop"
        ]
      },
      {
        "name": "SAP",
        "category": "Specialist / field-specific",
        "usage": "Specialist - ERP modules",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "sap-erp"
        ]
      },
      {
        "name": "Google Analytics",
        "category": "Specialist / field-specific",
        "usage": "Specialist - marketing analytics",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "google-analytics"
        ]
      },
      {
        "name": "NVivo",
        "category": "Specialist / field-specific",
        "usage": "Specialist - qualitative research",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "nvivo"
        ]
      },
      {
        "name": "Excel, SPSS, Tableau/Power BI",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel",
          "ibm-spss-statistics",
          "tableau-desktop",
          "power-bi-desktop"
        ]
      },
      {
        "name": "MS Office, EndNote / Mendeley",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365",
          "zotero"
        ]
      },
      {
        "name": "Bloomberg (finance electives), Stata/R, SPSS Amos",
        "category": "Elective-only",
        "usage": "Elective - finance & advanced analytics",
        "source": "U. Sheffield Accounting & Financial Management BA, sheffield.ac.uk/undergraduate/courses/2027/accounting-and-financial-management-ba",
        "catalogValues": [
          "bloomberg-terminal",
          "stata",
          "r-rstudio"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-excel",
      "microsoft-powerpoint",
      "ibm-spss-statistics",
      "tableau-desktop",
      "power-bi-desktop",
      "sap-erp",
      "google-analytics",
      "nvivo",
      "microsoft-365",
      "zotero",
      "bloomberg-terminal",
      "stata",
      "r-rstudio"
    ]
  },
  {
    "value": "accounting-finance",
    "label": "Accounting & Finance",
    "summary": "Covers Accounting, Finance, Accounting & Finance and related courses. Selecting this degree pre-fills the software typically used on the course — including Microsoft Excel (financial modelling), Bloomberg Terminal, Refinitiv Eikon / Datastream — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Accounting; Finance; Accounting & Finance; Actuarial Science (overlaps Maths)",
    "universities": "LSE, Loughborough, Bath, Surrey, Warwick, Exeter, Birmingham, Lancaster, Edinburgh, Durham",
    "software": [
      {
        "name": "Microsoft Excel (financial modelling)",
        "category": "Core tools",
        "usage": "Core, from Year 1 - advanced spreadsheeting",
        "source": "U. Sheffield Accounting & Financial Management BA, sheffield.ac.uk/undergraduate/courses/2027/accounting-and-financial-management-ba",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "Bloomberg Terminal",
        "category": "Core tools",
        "usage": "Core, Year 2-3 - trading room / market data",
        "source": "U. Bristol School of Economics Bloomberg Room, bristol.ac.uk/economics/why-bristol/bloomberg-room",
        "catalogValues": [
          "bloomberg-terminal"
        ]
      },
      {
        "name": "PowerPoint",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-powerpoint"
        ]
      },
      {
        "name": "Refinitiv Eikon / Datastream",
        "category": "Specialist / field-specific",
        "usage": "Specialist - market data",
        "source": "U. Sheffield Accounting & Financial Management BA, sheffield.ac.uk/undergraduate/courses/2027/accounting-and-financial-management-ba",
        "catalogValues": [
          "refinitiv-eikon"
        ]
      },
      {
        "name": "SAGE",
        "category": "Specialist / field-specific",
        "usage": "Specialist - accounting software",
        "source": "U. Roehampton Finance & Accounting (Bloomberg, SAGE, Excel), roehampton.ac.uk/study/undergraduate-courses/finance-and-accounting",
        "catalogValues": [
          "sage"
        ]
      },
      {
        "name": "Stata",
        "category": "Specialist / field-specific",
        "usage": "Specialist - empirical finance",
        "source": "Keele U. Accounting & Finance (Bloomberg Lab, Stata, Python), keele.ac.uk/study/undergraduate/undergraduatecourses/accountingandfinance",
        "catalogValues": [
          "stata"
        ]
      },
      {
        "name": "Excel, Stata/R, Bloomberg",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Keele U. Accounting & Finance, keele.ac.uk/study/undergraduate/undergraduatecourses/accountingandfinance",
        "catalogValues": [
          "microsoft-excel",
          "stata",
          "r-rstudio",
          "bloomberg-terminal"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Writing & productivity",
        "usage": "Writing - throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "Python (fintech), Capital IQ, ACL/IDEA (audit), VBA",
        "category": "Elective-only",
        "usage": "Elective - fintech & audit analytics",
        "source": "Keele U. Accounting & Finance, keele.ac.uk/study/undergraduate/undergraduatecourses/accountingandfinance",
        "catalogValues": [
          "python",
          "capital-iq"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-excel",
      "bloomberg-terminal",
      "microsoft-powerpoint",
      "refinitiv-eikon",
      "sage",
      "stata",
      "r-rstudio",
      "microsoft-365",
      "python",
      "capital-iq"
    ]
  },
  {
    "value": "politics-ir-government",
    "label": "Politics, IR & Government",
    "summary": "Covers Politics, International Relations, Government and related courses. Selecting this degree pre-fills the software typically used on the course — including Microsoft Office, Excel, R or Stata — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Politics; International Relations; Government; PPE; Political Economy",
    "universities": "LSE + broad-14 + St Andrews, Loughborough, Bath, Surrey",
    "software": [
      {
        "name": "Microsoft Office",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1 - data",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "R or Stata",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 2 - quantitative methods",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio",
          "stata"
        ]
      },
      {
        "name": "NVivo",
        "category": "Specialist / field-specific",
        "usage": "Specialist - qualitative analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "nvivo"
        ]
      },
      {
        "name": "Qualtrics",
        "category": "Specialist / field-specific",
        "usage": "Specialist - surveys",
        "source": "Computing for Psychologists, computingforpsychologists.wordpress.com/category/software",
        "catalogValues": [
          "qualtrics"
        ]
      },
      {
        "name": "R/Stata, Excel, SPSS",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio",
          "stata",
          "microsoft-excel",
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "Zotero / EndNote, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "QGIS (political geography), Python (text analysis)",
        "category": "Elective-only",
        "usage": "Elective - spatial & computational methods",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "qgis",
          "python"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-365",
      "microsoft-excel",
      "r-rstudio",
      "stata",
      "nvivo",
      "qualtrics",
      "ibm-spss-statistics",
      "zotero",
      "qgis",
      "python"
    ]
  },
  {
    "value": "sociology-anthropology-criminology-social-policy",
    "label": "Sociology, Anthropology, Criminology & Social Policy",
    "summary": "Covers Sociology, Social Anthropology, Criminology and related courses. Selecting this degree pre-fills the software typically used on the course — including SPSS, NVivo, R — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Sociology; Social Anthropology; Criminology; Social Policy; Social Work; Human/Social Sciences",
    "universities": "LSE + broad-14 + Loughborough, Surrey, Bath",
    "software": [
      {
        "name": "SPSS",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - quantitative methods",
        "source": "U. Roehampton Criminology & Sociology (SPSS, NVivo, R), roehampton.ac.uk/study/undergraduate-courses/criminology-and-sociology",
        "catalogValues": [
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "NVivo",
        "category": "Core tools",
        "usage": "Core, Year 2 - qualitative analysis",
        "source": "U. Roehampton Criminology & Sociology, roehampton.ac.uk/study/undergraduate-courses/criminology-and-sociology",
        "catalogValues": [
          "nvivo"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "R",
        "category": "Specialist / field-specific",
        "usage": "Specialist, later years - advanced statistics",
        "source": "U. Roehampton Criminology & Sociology, roehampton.ac.uk/study/undergraduate-courses/criminology-and-sociology",
        "catalogValues": [
          "r-rstudio"
        ]
      },
      {
        "name": "Qualtrics",
        "category": "Specialist / field-specific",
        "usage": "Specialist - surveys",
        "source": "Computing for Psychologists, computingforpsychologists.wordpress.com/category/software",
        "catalogValues": [
          "qualtrics"
        ]
      },
      {
        "name": "ATLAS.ti",
        "category": "Specialist / field-specific",
        "usage": "Specialist - qualitative alternative",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "atlas-ti"
        ]
      },
      {
        "name": "SPSS, R, NVivo",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "U. Roehampton Criminology & Sociology, roehampton.ac.uk/study/undergraduate-courses/criminology-and-sociology",
        "catalogValues": [
          "ibm-spss-statistics",
          "r-rstudio",
          "nvivo"
        ]
      },
      {
        "name": "Zotero / EndNote / Mendeley, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "GIS (crime mapping), Python",
        "category": "Elective-only",
        "usage": "Elective - spatial & computational",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "arcgis-pro",
          "python"
        ]
      }
    ],
    "softwareValues": [
      "ibm-spss-statistics",
      "nvivo",
      "microsoft-365",
      "r-rstudio",
      "qualtrics",
      "atlas-ti",
      "zotero",
      "arcgis-pro",
      "python"
    ]
  },
  {
    "value": "geography",
    "label": "Geography",
    "summary": "Covers Geography (BA/BSc), Human Geography, Physical Geography and related courses. Selecting this degree pre-fills the software typically used on the course — including ArcGIS / ArcGIS Pro, QGIS, Remote sensing (ENVI/ERDAS) — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Geography (BA/BSc); Human Geography; Physical Geography; Geography & Environment",
    "universities": "LSE + broad-14 + St Andrews, Loughborough",
    "software": [
      {
        "name": "ArcGIS / ArcGIS Pro",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - GIS",
        "source": "U. Cambridge Dept of Geography Computing/GIS, geog.cam.ac.uk/facilities/computing",
        "catalogValues": [
          "arcgis-pro"
        ]
      },
      {
        "name": "QGIS",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - open-source GIS",
        "source": "U. Edinburgh School of GeoSciences GIS suite, geosciences.ed.ac.uk/about/facilities/all/gis",
        "catalogValues": [
          "qgis"
        ]
      },
      {
        "name": "Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-excel"
        ]
      },
      {
        "name": "SPSS or R",
        "category": "Core tools",
        "usage": "Core, Year 2 - quantitative methods",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics",
          "r-rstudio"
        ]
      },
      {
        "name": "Remote sensing (ENVI/ERDAS)",
        "category": "Specialist / field-specific",
        "usage": "Specialist - physical geography",
        "source": "U. Cambridge Dept of Geography Computing, geog.cam.ac.uk/facilities/computing",
        "catalogValues": [
          "envi-erdas"
        ]
      },
      {
        "name": "Python",
        "category": "Specialist / field-specific",
        "usage": "Specialist - spatial analysis",
        "source": "U. Edinburgh School of GeoSciences GIS suite, geosciences.ed.ac.uk/about/facilities/all/gis",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "R, SPSS, Excel",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio",
          "ibm-spss-statistics",
          "microsoft-excel"
        ]
      },
      {
        "name": "EndNote / Mendeley, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "Google Earth Engine, NVivo (human geog), CAD",
        "category": "Elective-only",
        "usage": "Elective - cloud RS & qualitative",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "google-earth-engine",
          "nvivo"
        ]
      }
    ],
    "softwareValues": [
      "arcgis-pro",
      "qgis",
      "microsoft-excel",
      "ibm-spss-statistics",
      "r-rstudio",
      "envi-erdas",
      "python",
      "zotero",
      "microsoft-365",
      "google-earth-engine",
      "nvivo"
    ]
  },
  {
    "value": "environmental-policy-studies",
    "label": "Environmental Policy & Studies",
    "summary": "Covers Environmental Policy, Environmental Studies, Sustainable Development and related courses. Selecting this degree pre-fills the software typically used on the course — including Microsoft Office / Excel, QGIS / ArcGIS, SimaPro (life-cycle assessment) — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Environmental Policy; Environmental Studies; Sustainable Development; Geography with Environment (social-science side)",
    "universities": "LSE, St Andrews + broad-14 subset",
    "software": [
      {
        "name": "Microsoft Office / Excel",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365",
          "microsoft-excel"
        ]
      },
      {
        "name": "QGIS / ArcGIS",
        "category": "Core tools",
        "usage": "Core, Year 1-2 - GIS",
        "source": "U. Edinburgh School of GeoSciences GIS suite, geosciences.ed.ac.uk/about/facilities/all/gis",
        "catalogValues": [
          "qgis",
          "arcgis-pro"
        ]
      },
      {
        "name": "R or SPSS",
        "category": "Core tools",
        "usage": "Core, Year 2 - data analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio",
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "SimaPro (life-cycle assessment)",
        "category": "Specialist / field-specific",
        "usage": "Specialist - sustainability analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "simapro"
        ]
      },
      {
        "name": "Python",
        "category": "Specialist / field-specific",
        "usage": "Specialist - data analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "python"
        ]
      },
      {
        "name": "R, Excel, GIS",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio",
          "microsoft-excel",
          "arcgis-pro"
        ]
      },
      {
        "name": "EndNote / Mendeley, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "Energy/climate modelling, NVivo",
        "category": "Elective-only",
        "usage": "Elective - modelling & qualitative",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "nvivo"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-365",
      "microsoft-excel",
      "qgis",
      "arcgis-pro",
      "r-rstudio",
      "ibm-spss-statistics",
      "simapro",
      "python",
      "zotero",
      "nvivo"
    ]
  },
  {
    "value": "law",
    "label": "Law",
    "summary": "Covers Law (LLB), Law with a language/year abroad, Jurisprudence (Oxford) and related courses. Selecting this degree pre-fills the software typically used on the course — including Westlaw UK, LexisNexis / Lexis+ UK, Practical Law — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Law (LLB); Law with a language/year abroad; Jurisprudence (Oxford); Law with Business",
    "universities": "LSE + broad-14 + Surrey, Loughborough. Not Imperial, Bath, St Andrews",
    "software": [
      {
        "name": "Westlaw UK",
        "category": "Core tools",
        "usage": "Core, from Year 1 - legal research",
        "source": "UK university law library guides (Westlaw UK) - e.g. UCL, KCL, Bristol, Exeter",
        "catalogValues": [
          "legal-databases"
        ]
      },
      {
        "name": "LexisNexis / Lexis+ UK",
        "category": "Core tools",
        "usage": "Core, from Year 1 - legal research",
        "source": "UK university law library guides (Lexis+ UK) - e.g. UCL, KCL, Bristol",
        "catalogValues": [
          "legal-databases"
        ]
      },
      {
        "name": "Microsoft Office",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "Practical Law",
        "category": "Specialist / field-specific",
        "usage": "Specialist - practice notes & know-how",
        "source": "UK university law library guides (Practical Law)",
        "catalogValues": [
          "legal-databases"
        ]
      },
      {
        "name": "HeinOnline",
        "category": "Specialist / field-specific",
        "usage": "Specialist - legal journals & history",
        "source": "UK university law library guides (HeinOnline)",
        "catalogValues": [
          "legal-databases"
        ]
      },
      {
        "name": "OSCOLA referencing, MS Word",
        "category": "Writing & productivity",
        "usage": "Writing - legal citation",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-word"
        ]
      },
      {
        "name": "Legal-tech / e-discovery tools (clinics)",
        "category": "Elective-only",
        "usage": "Elective - law clinic & legal tech",
        "source": "Industry-standard tool for the field",
        "catalogValues": []
      }
    ],
    "softwareValues": [
      "legal-databases",
      "microsoft-365",
      "microsoft-word"
    ]
  },
  {
    "value": "humanities-essay-based",
    "label": "Humanities (essay-based)",
    "summary": "Covers History, English (Literature/Language), Philosophy and related courses. Selecting this degree pre-fills the software typically used on the course — including Microsoft Word, Reference manager (Zotero / EndNote), Digital humanities tools (Voyant, Omeka) — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "History; English (Literature/Language); Philosophy; Classics; Theology & Religious Studies; History of Art; Liberal Arts; Ancient History",
    "universities": "LSE (History, Philosophy) + broad-14 + St Andrews, Loughborough, Bath, Surrey (limited). Not Imperial",
    "software": [
      {
        "name": "Microsoft Word",
        "category": "Core tools",
        "usage": "Core, throughout - essays",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-word"
        ]
      },
      {
        "name": "Reference manager (Zotero / EndNote)",
        "category": "Core tools",
        "usage": "Core, from Year 1 - citations",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero"
        ]
      },
      {
        "name": "Library databases (JSTOR, etc.)",
        "category": "Core tools",
        "usage": "Core, from Year 1 - sources",
        "source": "Industry-standard tool for the field",
        "catalogValues": []
      },
      {
        "name": "Digital humanities tools (Voyant, Omeka)",
        "category": "Specialist / field-specific",
        "usage": "Specialist - some modules",
        "source": "Industry-standard tool for the field",
        "catalogValues": []
      },
      {
        "name": "Zotero / EndNote, MS Office, LaTeX (some)",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365",
          "overleaf"
        ]
      },
      {
        "name": "GIS (historical), TEI/XML, image databases",
        "category": "Elective-only",
        "usage": "Elective - digital humanities",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "arcgis-pro"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-word",
      "zotero",
      "microsoft-365",
      "overleaf",
      "arcgis-pro"
    ]
  },
  {
    "value": "modern-languages-linguistics",
    "label": "Modern Languages & Linguistics",
    "summary": "Covers Modern Languages, French/German/Spanish etc., Linguistics and related courses. Selecting this degree pre-fills the software typically used on the course — including Microsoft Word (incl. non-Latin scripts), Reference manager (Zotero / EndNote), Praat — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Modern Languages; French/German/Spanish etc.; Linguistics; English Language & Linguistics; Area Studies",
    "universities": "Broad-14 + St Andrews, Bath, Surrey. Not Imperial, LSE",
    "software": [
      {
        "name": "Microsoft Word (incl. non-Latin scripts)",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-word"
        ]
      },
      {
        "name": "Reference manager (Zotero / EndNote)",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero"
        ]
      },
      {
        "name": "Praat",
        "category": "Specialist / field-specific",
        "usage": "Specialist - phonetics analysis",
        "source": "U. Glasgow Laboratory of Phonetics (GULP) software (Praat), gla.ac.uk/schools/critical/aboutus/resources/gulp",
        "catalogValues": [
          "praat"
        ]
      },
      {
        "name": "AntConc",
        "category": "Specialist / field-specific",
        "usage": "Specialist - corpus analysis",
        "source": "U. Reading, Introduction to Corpus Linguistics module LS3ICL, reading.ac.uk/modules",
        "catalogValues": [
          "antconc"
        ]
      },
      {
        "name": "ELAN",
        "category": "Specialist / field-specific",
        "usage": "Specialist - annotation of audio/video",
        "source": "U. Manchester Linguistics facilities, humanities.manchester.ac.uk/linguistics-and-english-language/study/facilities",
        "catalogValues": [
          "elan"
        ]
      },
      {
        "name": "R",
        "category": "Specialist / field-specific",
        "usage": "Specialist - quantitative linguistics",
        "source": "U. Manchester Linguistics facilities, humanities.manchester.ac.uk/linguistics-and-english-language/study/facilities",
        "catalogValues": [
          "r-rstudio"
        ]
      },
      {
        "name": "R, Excel",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio",
          "microsoft-excel"
        ]
      },
      {
        "name": "Zotero / EndNote, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "Python (NLP), Sketch Engine, SDL Trados (translation)",
        "category": "Elective-only",
        "usage": "Elective - computational linguistics & translation",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "python",
          "sdl-trados"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-word",
      "zotero",
      "praat",
      "antconc",
      "elan",
      "r-rstudio",
      "microsoft-excel",
      "microsoft-365",
      "python",
      "sdl-trados"
    ]
  },
  {
    "value": "archaeology",
    "label": "Archaeology",
    "summary": "Covers Archaeology, Archaeology & Anthropology, Bioarchaeology. Selecting this degree pre-fills the software typically used on the course — including QGIS / ArcGIS, Microsoft Office / Excel, Agisoft Metashape — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Archaeology; Archaeology & Anthropology; Bioarchaeology",
    "universities": "Cambridge, Oxford, UCL, Durham, Exeter, York, Edinburgh, Southampton",
    "software": [
      {
        "name": "QGIS / ArcGIS",
        "category": "Core tools",
        "usage": "Core, Year 2 - GIS",
        "source": "UCL Archaeological & GIS Laboratory (ArcGIS, QGIS, Inkscape), ucl.ac.uk/archaeology/research/facilities/archaeological-and-geographical-information-systems-laboratory",
        "catalogValues": [
          "qgis",
          "arcgis-pro"
        ]
      },
      {
        "name": "Microsoft Office / Excel",
        "category": "Core tools",
        "usage": "Core, from Year 1",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365",
          "microsoft-excel"
        ]
      },
      {
        "name": "Agisoft Metashape",
        "category": "Specialist / field-specific",
        "usage": "Specialist - photogrammetry",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "agisoft-metashape"
        ]
      },
      {
        "name": "Inkscape / Adobe Illustrator",
        "category": "Specialist / field-specific",
        "usage": "Specialist - archaeological illustration",
        "source": "UCL Archaeological & GIS Laboratory, ucl.ac.uk/archaeology/research/facilities/archaeological-and-geographical-information-systems-laboratory",
        "catalogValues": [
          "inkscape",
          "adobe-illustrator"
        ]
      },
      {
        "name": "GIS for landscape/spatial analysis",
        "category": "Specialist / field-specific",
        "usage": "Specialist, Year 3 - spatial analysis module",
        "source": "U. Southampton module ARCH3044 GIS for Archaeology, southampton.ac.uk/courses/2026-27/modules/arch3044",
        "catalogValues": []
      },
      {
        "name": "R, Excel, GIS",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "U. Edinburgh course ARCA10086 GIS for Archaeologists, drps.ed.ac.uk",
        "catalogValues": [
          "r-rstudio",
          "microsoft-excel",
          "arcgis-pro"
        ]
      },
      {
        "name": "Zotero / EndNote, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "Blender (3D), geophysics software, PAST (stats)",
        "category": "Elective-only",
        "usage": "Elective - 3D, geophysics & quantitative",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "blender"
        ]
      }
    ],
    "softwareValues": [
      "qgis",
      "arcgis-pro",
      "microsoft-365",
      "microsoft-excel",
      "agisoft-metashape",
      "inkscape",
      "adobe-illustrator",
      "r-rstudio",
      "zotero",
      "blender"
    ]
  },
  {
    "value": "education",
    "label": "Education",
    "summary": "Covers Education, Education Studies, Childhood/Early Years. Selecting this degree pre-fills the software typically used on the course — including Microsoft Office, SPSS, NVivo — so your laptop match reflects what you'll actually run, from first-year labs to your dissertation.",
    "degrees": "Education; Education Studies; Childhood/Early Years",
    "universities": "Cambridge, Birmingham, Edinburgh, Exeter, York, Durham",
    "software": [
      {
        "name": "Microsoft Office",
        "category": "Core tools",
        "usage": "Core, throughout",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "microsoft-365"
        ]
      },
      {
        "name": "SPSS",
        "category": "Core tools",
        "usage": "Core, Year 2 - research methods",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics"
        ]
      },
      {
        "name": "NVivo",
        "category": "Specialist / field-specific",
        "usage": "Specialist - qualitative analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "nvivo"
        ]
      },
      {
        "name": "Qualtrics",
        "category": "Specialist / field-specific",
        "usage": "Specialist - surveys",
        "source": "Computing for Psychologists, computingforpsychologists.wordpress.com/category/software",
        "catalogValues": [
          "qualtrics"
        ]
      },
      {
        "name": "SPSS, Excel, R",
        "category": "Data / analysis tools",
        "usage": "Data - analysis",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "ibm-spss-statistics",
          "microsoft-excel",
          "r-rstudio"
        ]
      },
      {
        "name": "Zotero / EndNote / Mendeley, MS Office",
        "category": "Writing & productivity",
        "usage": "Writing - referencing",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "zotero",
          "microsoft-365"
        ]
      },
      {
        "name": "Classroom technology tools, R",
        "category": "Elective-only",
        "usage": "Elective - edtech & advanced stats",
        "source": "Industry-standard tool for the field",
        "catalogValues": [
          "r-rstudio"
        ]
      }
    ],
    "softwareValues": [
      "microsoft-365",
      "ibm-spss-statistics",
      "nvivo",
      "qualtrics",
      "microsoft-excel",
      "r-rstudio",
      "zotero"
    ]
  }
];
